import { format } from 'date-fns';
import numeral from 'numeral';
import { NOT_APPLICABLE_MSG, PENDING_MSG, VOUCHER_COVERS_RENT } from '../../../../constants/messages';
import { applicantStatusTypes } from '../../../../constants/statusConstants';
import { getHasPendingApplicants } from '../../../../utils/applicants';
import FormatLogic from '../../../../utils/formatLogicUtil';

export function getTotalIncome(applicants, incomeSourceTypes = null) {
  return applicants
    .flatMap((applicant) =>
      applicant.answers.currentIncomes
        .filter((currentIncome) => (incomeSourceTypes ? incomeSourceTypes.includes(currentIncome.type) : true))
        .map((currentIncome) => numeral(currentIncome.amount).value()),
    )
    .reduce((acc, value) => acc + value, 0);
}

export function getTotalIncomeRollup(applicants, invitations, { includeSuffix = true } = {}) {
  if (
    getHasPendingApplicants(applicants) ||
    invitations.length > 0 ||
    applicants.every((applicant) => applicant.answers.currentIncomes.length === 0)
  ) {
    return PENDING_MSG;
  }

  const totalIncome = getTotalIncome(applicants);
  return includeSuffix ? FormatLogic.monthlyIncome(totalIncome) : FormatLogic.money(totalIncome);
}

export function getIncomeToRentRatioRollup(applicants, invitations, monthlyPayment) {
  if (invitations.length > 0) {
    return PENDING_MSG;
  }
  if (!applicants.every((applicant) => applicant.status === applicantStatusTypes.COMPLETED.id)) {
    return PENDING_MSG;
  }
  if (applicants.every((applicant) => applicant.answers.currentIncomes.length === 0)) {
    return PENDING_MSG;
  }
  const totalIncome = getTotalIncome(applicants);
  const voucherIncome = getTotalIncome(applicants, ['voucher']);
  const rentPrice = getRentPriceAsDollars(monthlyPayment);

  if (rentPrice <= 0) {
    return PENDING_MSG;
  }

  if (voucherIncome >= rentPrice) {
    return VOUCHER_COVERS_RENT;
  }

  const ratio =
    totalIncome === voucherIncome
      ? totalIncome / rentPrice
      : (totalIncome - voucherIncome) / (rentPrice - voucherIncome);

  return (
    numeral(ratio)
      .format('0.00')
      .replace(/(\.0+|0)$/, '') + 'X'
  );
}

export function getMoveInDateRollup(applicants) {
  const sortedApplicants = [...applicants]
    .filter((applicant) => applicant.answers.moveInDate)
    .sort((lhs, rhs) => lhs.moveInDate - rhs.moveInDate);

  const firstMoveInDate = sortedApplicants[0]?.answers.moveInDate;

  if (!firstMoveInDate) {
    return NOT_APPLICABLE_MSG;
  }

  // If any applicant has a flexible move in date, then show "Flexible"
  const isMoveInDateFlexible = sortedApplicants.some((applicant) => applicant.answers.isMoveInDateFlexible);
  const formattedMoveInDate = format(firstMoveInDate, 'MM/DD/YYYY');
  return isMoveInDateFlexible ? `${formattedMoveInDate} (Flexible)` : formattedMoveInDate;
}

export function getOccupantsRollup(applicants) {
  const occupants = Math.max(
    ...applicants
      .filter((applicant) => Number.isInteger(applicant.answers.occupants))
      .map((applicant) => applicant.answers.occupants),
    1, // defaulting to 1 person
  );

  return `${occupants} ${occupants === 1 ? 'person' : 'people'}`;
}

export function getHasSmokerRollup(applicants) {
  const hasSmoker = applicants.some((applicant) => applicant.answers.isSmoker);
  return hasSmoker ? 'Yes' : 'No';
}

export function getRentPriceAsDollars(monthlyPayment) {
  return numeral(monthlyPayment).value();
}
